import { Injectable } from '@angular/core';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  NavigationEnd,
  NavigationExtras,
  PRIMARY_OUTLET,
  Router,
  RouterOutlet,
  UrlSegmentGroup,
} from '@angular/router';
import { takePromise } from '@examdojo/util/take-promise';
import {
  BehaviorSubject,
  distinctUntilChanged,
  filter,
  map,
  merge,
  Observable,
  pairwise,
  shareReplay,
  startWith,
  switchMap,
  withLatestFrom,
} from 'rxjs';

@Injectable({ providedIn: 'root' })
export class NamedRouterOutletService {
  constructor(private readonly router: Router) {}

  private readonly namedRouterOutlets$$: BehaviorSubject<
    Record<
      string,
      {
        outlet: RouterOutlet;
        route: ActivatedRoute;
      }
    >
  > = new BehaviorSubject({});

  readonly navigationEndEvents = this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd));

  readonly primaryOutletState$: Observable<ActivatedRouteSnapshot> = this.router.events.pipe(
    filter((e): e is NavigationEnd => e instanceof NavigationEnd),
    startWith(true),
    map(() => this.router.routerState.root.snapshot),
  );

  readonly primaryOutletUrl$: Observable<string> = this.primaryOutletState$.pipe(
    map((state) => this.getPrimaryOutletUrl(state)),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  /**
   * Emits on URL changes on the **primary** `router-outlet`.
   * Useful to listen to navigation events of only the primary `router-outlet`.
   */
  readonly primaryOutletNavigated$: Observable<void> = this.primaryOutletState$.pipe(
    pairwise(),
    map(
      ([previousState, currentState]) =>
        this.getPrimaryOutletUrl(previousState) !== this.getPrimaryOutletUrl(currentState),
    ),
    filter(Boolean),
    map(() => undefined),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  /**
   * Register a named `router-outlet` and its corresponding root `ActivatedRoute`
   */
  register(outletName: string, outlet: RouterOutlet, route: ActivatedRoute) {
    const namedRouterOutlets = this.namedRouterOutlets$$.value;
    namedRouterOutlets[outletName] = { outlet, route };
    this.namedRouterOutlets$$.next(namedRouterOutlets);
  }

  /**
   * Emits a boolean if the named router-outlet is active or not
   */
  selectIsPathActive(outletName: string, path: string): Observable<boolean> {
    return this.selectOutletActivated(outletName).pipe(
      withLatestFrom(this.selectOutlet(outletName)),
      map(([open, outlet]) => open && outlet.activatedRoute.routeConfig?.path === path),
      distinctUntilChanged(),
    );
  }

  /**
   * Emits a boolean whenever the named `router`outlet`'s state changes,
   * whether a route is activated (true) or deactivated (false).
   */
  selectOutletActivated(outletName: string): Observable<boolean> {
    return this.selectOutlet(outletName).pipe(
      switchMap((outlet) =>
        merge(outlet.activateEvents, outlet.deactivateEvents).pipe(
          startWith(outlet.isActivated),
          map(() => this.getActivatedRoute(outletName)),
          map((route) => route?.children ?? []),
          map((children) => children.some((child) => child.outlet === outletName)),
          distinctUntilChanged(),
        ),
      ),
    );
  }

  /**
   * Select a named router-outlet's first child `ActivatedRoute`.
   */
  selectChildActivatedRoute(outletName: string): Observable<ActivatedRoute | null> {
    return this.selectOutletActivated(outletName).pipe(
      withLatestFrom(this.selectOutlet(outletName)),
      map(([_, outlet]) => {
        try {
          return outlet.activatedRoute.firstChild || null;
        } catch (err) {
          return null;
        }
      }),
    );
  }

  /**
   * Select a named router-outlet's root `ActivatedRoute`.
   */
  selectedRootActivatedRoute(outletName: string) {
    return this.namedRouterOutlets$$.pipe(
      map(() => this.getActivatedRoute(outletName)),
      filter(Boolean),
      distinctUntilChanged(),
    );
  }

  async navigate(outletName: string, commands: string[] | null, options?: NavigationExtras): Promise<boolean> {
    let relativeTo = options?.relativeTo;

    if (!relativeTo) {
      relativeTo = await takePromise(this.selectedRootActivatedRoute(outletName));
    }

    return await this.router.navigate(
      [
        {
          outlets: { [outletName]: commands },
        },
      ],
      {
        // !!! IMPORTANT !!!
        // The correct `relativeTo` `ActivatedRoute` object is the **key for correct navigation** in secondary router-outlets (sidebar, bottom_drawer).
        //
        // With the current route configuration and hierarchy in `feature.module` the correct `ActivatedRoute` object is the *parent* of the `FeatureComponent`.
        // If routing in the sidebar or bottom_drawer breaks play around with either `relativeTo.parent` or `firstChild` or `children`.
        relativeTo,
        queryParamsHandling: 'preserve',
        ...(options || {}),
      },
    );
  }

  getPrimaryOutletUrl(
    routeOrUrl: ActivatedRouteSnapshot | string | undefined = this.router.routerState.root.snapshot,
  ): string {
    if (typeof routeOrUrl === 'string') {
      const segments: string[] = [];
      const traversePrimaryUrlSegmentGroup = (segmentGroup: UrlSegmentGroup | undefined) => {
        if (!segmentGroup) {
          return;
        }

        segmentGroup.segments.forEach((segment) => segments.push(segment.path));

        if (segmentGroup.children['primary']) {
          traversePrimaryUrlSegmentGroup(segmentGroup.children['primary']);
        }
      };

      traversePrimaryUrlSegmentGroup(this.router.parseUrl(routeOrUrl).root?.children['primary']);

      return `/${segments.join('/')}`;
    }

    let route = routeOrUrl;
    const urlSegments = [];
    while (route && (route.url.length > 0 || this.getPrimaryChild(route))) {
      urlSegments.push(...route.url);
      route = this.getPrimaryChild(route)!;
    }

    const primaryUrl = urlSegments
      .map((s) => s.path)
      .filter(Boolean)
      .join('/');

    return `/${primaryUrl}`;
  }

  /**
   * Get a named router-outlet's corresponding `ActivatedRoute`.
   */
  getActivatedRoute(outletName: string) {
    return this.namedRouterOutlets$$.value[outletName]?.route;
  }

  /**
   * Select a `router-outlet` by name.
   */
  private selectOutlet(outletName: string): Observable<RouterOutlet> {
    return this.namedRouterOutlets$$.pipe(
      map(() => this.getOutlet(outletName)),
      filter(Boolean),
      distinctUntilChanged(),
    );
  }

  /**
   * Get a `router-outlet` by name.
   */
  private getOutlet(outletName: string) {
    return this.namedRouterOutlets$$.value[outletName]?.outlet;
  }

  private getPrimaryChild(route: ActivatedRouteSnapshot | undefined): ActivatedRouteSnapshot | undefined {
    return route?.children?.find((c) => c.outlet === PRIMARY_OUTLET);
  }
}
